import type { HTMLAttributes } from "react";

import { cx } from "@/lib/cx";
import { TONE_SOFT, type Tone } from "./tones";

/** Kept as its own name because it is spelled at ~40 call sites. */
export type BadgeTone = Tone;

export type BadgeSize = "sm" | "md";

export type BadgeProps = HTMLAttributes<HTMLSpanElement> & {
  tone?: BadgeTone;
  size?: BadgeSize;
  /** Use for counts and durations so the digits keep a fixed width. */
  numeric?: boolean;
};

/**
 * Badges are static status marks, so they stay compact: 24px for dense status
 * rows and 32px for an ordinary labelled badge.
 */
const SIZES: Record<BadgeSize, string> = {
  sm: "h-6 gap-1 px-2 text-caption",
  md: "h-8 gap-2 px-3 text-label",
};

/**
 * A small status pill: an overdue count, a streak, a priority.
 *
 * The most common element in the app, and its most common variant — the
 * overdue `26d` pill — used to measure 2.68:1 in light. The colours here come
 * from `tones.ts` rather than being spelled out again, so a badge cannot drift
 * away from the chip and the alert that are meant to be the same thing.
 *
 * A badge is *static*. Anything clickable is a {@link Chip}.
 */
export function Badge({
  tone = "neutral",
  size = "md",
  numeric = false,
  className,
  ...rest
}: BadgeProps) {
  return (
    <span
      className={cx(
        "busee-badge inline-flex max-w-full items-center rounded-control whitespace-nowrap",
        SIZES[size],
        TONE_SOFT[tone],
        numeric && "numeric",
        className,
      )}
      {...rest}
    />
  );
}
